import { createTheme } from "@mui/material/styles";

// ProCogia + Auth0 Branding Colors (matching Auth0 customization)
export const procogiaBrand = {
  // Primary colors from Auth0 branding
  green: {
    main: '#95c100', // Primary button color from Auth0
    light: '#a8d333',
    dark: '#7ea000',
    rgb: 'rgb(149, 193, 0)',
  },
  blue: {
    main: '#1fb8cc', // Links & focused components from Auth0
    light: '#4fc3d7',
    dark: '#1590a3',
    rgb: 'rgb(31, 184, 204)',
  },
  // Gray scale from Auth0 branding
  gray: {
    darkest: '#000000',    // Base Hover Color from Auth0
    dark: '#1e212a',       // Secondary button label from Auth0
    medium: '#333333',     // Header/Body text from Auth0
    light: '#65676e',      // Input labels & placeholders from Auth0
    lighter: '#c9cace',    // Secondary button border from Auth0
    lightest: '#ffffff',   // Primary button label from Auth0
  },
  // Auth0 specific colors
  auth0: {
    primaryButton: '#95c100',
    primaryButtonLabel: '#ffffff',
    secondaryButtonBorder: '#c9cace', 
    secondaryButtonLabel: '#1e212a',
    baseFocus: '#333333',
    baseHover: '#000000',
    links: '#1fb8cc',
    header: '#333333',
    body: '#333333',
    inputLabels: '#65676e',
    inputFilled: '#000000',
    inputBorder: '#c9cace',
    inputBackground: '#ffffff',
    icons: '#65676e',
    error: '#1fb8cc',
    success: '#95c100',
    widgetBorder: '#c9cace',
    widgetBackground: '#ffffff',
  },
  gradients: {
    primary: 'linear-gradient(135deg, #1fb8cc 0%, #95c100 100%)',
    secondary: 'linear-gradient(135deg, #95c100 0%, #7ea000 100%)',
    dark: 'linear-gradient(135deg, #333333 0%, #1e212a 100%)',
    button: 'linear-gradient(135deg, #95c100 0%, #a8d333 100%)',
  },
  status: {
    success: '#95c100',
    warning: '#1fb8cc',  // Changed from orange to ProCogia blue
    error: '#1fb8cc',
    info: '#1fb8cc',
  }
};

// ProCogia Material-UI Theme (Auth0 Branded)
export const procogiaMuiTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: procogiaBrand.green.main,      // Primary green from Auth0
      light: procogiaBrand.green.light,
      dark: procogiaBrand.green.dark,
      contrastText: procogiaBrand.gray.lightest,
    },
    secondary: {
      main: procogiaBrand.blue.main,       // Secondary blue from Auth0
      light: procogiaBrand.blue.light,
      dark: procogiaBrand.blue.dark,
      contrastText: procogiaBrand.gray.lightest,
    },
    background: {
      default: '#f8fafc',
      paper: procogiaBrand.auth0.widgetBackground,
    },
    text: {
      primary: procogiaBrand.auth0.body,         // #333333 from Auth0
      secondary: procogiaBrand.gray.light,       // #65676e from Auth0
    },
    success: {
      main: procogiaBrand.auth0.success,
      contrastText: procogiaBrand.gray.lightest,
    },
    warning: {
      main: procogiaBrand.status.warning,
      contrastText: procogiaBrand.gray.lightest,
    },
    error: {
      main: procogiaBrand.auth0.error,
      contrastText: procogiaBrand.gray.lightest,
    },
    info: {
      main: procogiaBrand.auth0.links,
      contrastText: procogiaBrand.gray.lightest,
    },
    // Custom palette additions for Auth0 consistency
    action: {
      hover: 'rgba(0, 0, 0, 0.04)',
      selected: 'rgba(149, 193, 0, 0.08)',
      focus: 'rgba(51, 51, 51, 0.12)',
    },
  },
  typography: {
    fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '2.5rem',
      color: procogiaBrand.auth0.header,
      lineHeight: 1.2,
    },
    h2: {
      fontWeight: 600,
      fontSize: '2rem',
      color: procogiaBrand.auth0.header,
      lineHeight: 1.3,
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.75rem',
      color: procogiaBrand.auth0.header,
      lineHeight: 1.3,
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
      color: procogiaBrand.auth0.header,
      lineHeight: 1.4,
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.25rem',
      color: procogiaBrand.auth0.header,
      lineHeight: 1.4,
    },
    h6: {
      fontWeight: 600,
      fontSize: '1rem',
      color: procogiaBrand.auth0.header,
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '1rem',
      color: procogiaBrand.auth0.body,
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.875rem',
      color: procogiaBrand.auth0.body,
      lineHeight: 1.6,
    },
    button: {
      fontWeight: 600,
      textTransform: 'none',
      fontSize: '1rem',
    },
    caption: {
      fontSize: '0.75rem',
      color: procogiaBrand.gray.light,
      lineHeight: 1.4,
    },
  },
  shape: {
    borderRadius: 8, // Consistent border radius
  },
  components: {
    // Primary Button (Auth0 styled)
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '12px 24px',
          fontWeight: 600,
          textTransform: 'none',
          fontSize: '1rem',
          boxShadow: 'none',
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            transform: 'translateY(-1px)',
          },
        },
        contained: {
          backgroundColor: procogiaBrand.auth0.primaryButton,
          color: procogiaBrand.auth0.primaryButtonLabel,
          '&:hover': {
            backgroundColor: procogiaBrand.green.dark,
            filter: 'brightness(1.05)',
          },
        },
        outlined: {
          borderColor: procogiaBrand.auth0.secondaryButtonBorder,
          color: procogiaBrand.auth0.secondaryButtonLabel,
          backgroundColor: procogiaBrand.auth0.widgetBackground,
          '&:hover': {
            borderColor: procogiaBrand.auth0.baseFocus,
            backgroundColor: 'rgba(149, 193, 0, 0.04)',
          },
        },
        text: {
          color: procogiaBrand.auth0.links,
          '&:hover': {
            backgroundColor: 'rgba(31, 184, 204, 0.04)',
          },
        },
      },
    },
    // Card components (Auth0 Widget styled)
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundColor: procogiaBrand.auth0.widgetBackground,
          border: `1px solid ${procogiaBrand.auth0.widgetBorder}`,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
          },
        },
      },
    },
    // Input fields (Auth0 styled)
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            backgroundColor: procogiaBrand.auth0.inputBackground,
            '& fieldset': {
              borderColor: procogiaBrand.auth0.inputBorder,
            },
            '&:hover fieldset': {
              borderColor: procogiaBrand.auth0.baseFocus,
            },
            '&.Mui-focused fieldset': {
              borderColor: procogiaBrand.auth0.primaryButton,
              borderWidth: 2,
            },
          },
          '& .MuiInputLabel-root': {
            color: procogiaBrand.auth0.inputLabels,
            '&.Mui-focused': {
              color: procogiaBrand.auth0.primaryButton,
            },
          },
          '& .MuiOutlinedInput-input': {
            color: procogiaBrand.auth0.inputFilled,
          },
        },
      },
    },
    // Links (Auth0 styled)
    MuiLink: {
      styleOverrides: {
        root: {
          color: procogiaBrand.auth0.links,
          textDecoration: 'none',
          fontWeight: 500,
          '&:hover': {
            textDecoration: 'underline',
            color: procogiaBrand.blue.dark,
          },
        },
      },
    },
    // Chip components
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 500,
          fontSize: '0.875rem',
        },
        filled: {
          backgroundColor: procogiaBrand.auth0.primaryButton,
          color: procogiaBrand.auth0.primaryButtonLabel,
        },
        outlined: {
          borderColor: procogiaBrand.auth0.widgetBorder,
          color: procogiaBrand.auth0.body,
        },
      },
    },
    // App Bar (Header)
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: procogiaBrand.auth0.widgetBackground,
          color: procogiaBrand.auth0.header,
          boxShadow: `0 2px 8px rgba(0,0,0,0.1)`,
          borderBottom: `1px solid ${procogiaBrand.auth0.widgetBorder}`,
        },
      },
    },
    // Drawer (Sidebar)
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: procogiaBrand.auth0.header,
          color: procogiaBrand.gray.lightest,
          borderRight: `1px solid ${procogiaBrand.auth0.widgetBorder}`,
        },
      },
    },
    // Paper components
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: procogiaBrand.auth0.widgetBackground,
          borderRadius: 8,
        },
        elevation1: {
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        },
        elevation2: {
          boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
        },
        elevation3: {
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
        },
      },
    },
    // Typography components
    MuiTypography: {
      styleOverrides: {
        root: {
          '&.MuiTypography-caption': {
            color: procogiaBrand.gray.light,
          },
        },
      },
    },
  },
});

export default procogiaMuiTheme;
